import { uploadFileToFirebase } from "@/lib/firebase/mediaUploading";
import React from "react";
import { toast } from "sonner";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./collapsible";
import { Button } from "./button";
import { ChevronsUpDown } from "lucide-react";
import { Input } from "./input";
import MediaLibraryDialog from "./MediaLibraryDialog";

function MediaFormField({
  media,
  setMedia,
  setMediaUrl,
  refPath,
  uploadDisabled,
  ...props
}: {
  media: File | null;
  setMedia: React.Dispatch<React.SetStateAction<File | null>>;
  setMediaUrl: React.Dispatch<React.SetStateAction<string>>;
  refPath: string;
  uploadDisabled?: boolean;
} & React.ComponentPropsWithoutRef<"input">) {
  const [isUploading, setIsUploading] = React.useState(false),
    [isUploadSuccess, setIsUploadSuccess] = React.useState(false),
    [isOpen, setIsOpen] = React.useState(false);
  const [libraryOpen, setLibraryOpen] = React.useState(false);

  async function handleMediaUpload() {
    try {
      if (!media) return;
      setIsUploading(true);
      const mediaUrl = await uploadFileToFirebase(media, refPath);
      setMediaUrl(mediaUrl);
      setIsUploading(false);
      setIsUploadSuccess(true);
      toast.success("Media uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload media");
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center gap-1">
          <CollapsibleTrigger asChild>
            <Button
              variant={"ghost"}
              size={"icon"}
              className={`size-8 hover:bg-green-300 ${media ? "" : "hidden"}`}
            >
              <ChevronsUpDown />
            </Button>
          </CollapsibleTrigger>
          <Input
            type="file"
            accept={props.accept || "image/*,video/*"}
            onChange={(e) => {
              const file = e.target.files?.[0];
              setMedia(file || null);
              setIsOpen(true);
            }}
            {...props}
          />
          <Button
            type="button"
            disabled={uploadDisabled || isUploading || media == null || isUploadSuccess}
            className="ml-10"
            onClick={handleMediaUpload}
          >
            {isUploading
              ? "Uploading Media"
              : isUploadSuccess
              ? "Uploaded Successfully"
              : "Upload Media"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={uploadDisabled}
            className="ml-2"
            onClick={() => setLibraryOpen(true)}
          >
            Upload from Library
          </Button>
        </div>
        <CollapsibleContent>
          <div className="mt-4">
            {media?.type.startsWith("video/") ? (
              <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-md overflow-hidden">
                <video
                  src={media ? URL.createObjectURL(media) : undefined}
                  autoPlay
                  muted
                  controls
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-md overflow-hidden">
                <img
                  src={media ? URL.createObjectURL(media) : undefined}
                  className="w-full h-full object-cover"
                  alt="Selected preview"
                />
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
      <MediaLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        refPath={refPath}
        onSelect={(url) => {
          setMediaUrl(url);
          setIsUploadSuccess(true);
          setLibraryOpen(false);
          toast.success("Selected from library");
        }}
        title="Media Library"
      />
    </>
  );
}

export default MediaFormField;
